import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Field, Input, MessageBar, MessageBarBody, Spinner, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { api } from '../api';
import { useAuth } from '../auth';

const useStyles = makeStyles({
  wrap: { minHeight: '100vh', display: 'grid', placeItems: 'center', ...shorthands.padding('24px'), backgroundColor: tokens.colorNeutralBackground2 },
  card: { width: '100%', maxWidth: '400px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('16px'), ...shorthands.padding('28px') },
  brand: { display: 'flex', alignItems: 'center', ...shorthands.gap('10px') },
  logo: { width: '28px', height: '28px' },
  qr: { justifySelf: 'center', width: '176px', height: '176px', ...shorthands.borderRadius('8px'), backgroundColor: '#ffffff', ...shorthands.padding('6px') },
  secret: { fontFamily: tokens.fontFamilyMonospace, overflowWrap: 'anywhere', fontSize: tokens.fontSizeBase200 },
});

/** Password, then the authenticator code. The first sign-in sets the authenticator app up. */
export function LoginPage() {
  const s = useStyles();
  const { state } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const step = state?.state === 'mfa' ? 'mfa' : state?.state === 'enrol' ? 'enrol' : 'password';
  const done = () => qc.invalidateQueries({ queryKey: ['auth'] });
  // 401/409 here mean the sign-in this page was showing no longer exists (another tab signed in or out): ask again so the right step appears.
  const fail = (e: unknown) => {
    setError((e as Error).message);
    const status = (e as { status?: number }).status;
    if (status === 401 || status === 409) done();
  };

  const login = useMutation({ mutationFn: () => api('/auth/login', { method: 'POST', body: { email, password } }), onSuccess: () => (setError(null), setPassword(''), done()), onError: fail });
  const verify = useMutation({ mutationFn: () => api(`/auth/${step === 'enrol' ? 'enrol' : 'mfa'}`, { method: 'POST', body: { code } }), onSuccess: () => (setError(null), setCode(''), done()), onError: fail });
  const enrol = useQuery({ queryKey: ['enrol'], queryFn: () => api<{ secret: string; qr: string }>('/auth/enrol'), enabled: step === 'enrol', staleTime: Infinity });
  const back = async () => {
    await api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    setError(null);
    await done();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (step === 'password') login.mutate();
    else verify.mutate();
  };

  return (
    <div className={s.wrap}>
      <Card className={s.card}>
        <div className={s.brand}>
          <img src="/favicon.svg" alt="" className={s.logo} />
          <Text size={500} weight="semibold">Voxshift Hub</Text>
        </div>
        <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
          {step === 'password' && (
            <>
              <Text>Sign in to open Voxshift instances.</Text>
              <Field label="Email">
                <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} autoComplete="username" autoFocus />
              </Field>
              <Field label="Password">
                <Input type="password" value={password} onChange={(_, d) => setPassword(d.value)} autoComplete="current-password" />
              </Field>
            </>
          )}
          {step === 'enrol' && (
            <>
              <Text weight="semibold">Set up your authenticator app</Text>
              <Text>Scan this code with an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password), then enter the 6-digit code it shows.</Text>
              {enrol.isLoading ? <Spinner /> : enrol.data ? (
                <>
                  <img className={s.qr} src={enrol.data.qr} alt="QR code for your authenticator app" />
                  <Text size={200}>Cannot scan? Enter this key instead: <span className={s.secret}>{enrol.data.secret}</span></Text>
                </>
              ) : null}
            </>
          )}
          {step === 'mfa' && <Text>Enter the 6-digit code from your authenticator app.</Text>}
          {step !== 'password' && (
            <Field label="6-digit code">
              <Input value={code} onChange={(_, d) => setCode(d.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus />
            </Field>
          )}
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
          <Button appearance="primary" type="submit" disabled={login.isPending || verify.isPending || (step === 'password' ? !email || !password : code.length !== 6)}>
            {step === 'password' ? 'Continue' : step === 'enrol' ? 'Finish set up' : 'Sign in'}
          </Button>
          {step !== 'password' && (
            <Button appearance="subtle" type="button" onClick={() => void back()}>
              Use a different account
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}
